/**
 * Factory write actions - Market creation operations
 *
 * These require the factory owner wallet and are used for deploying new markets.
 */
import {
	type Address,
	type Client,
	encodeFunctionData,
	type Hash,
	type Hex,
	type WalletClient,
} from "viem"
import { readContract, waitForTransactionReceipt, writeContract } from "viem/actions"
import { breakoutMarketAbi } from "../../abi/generated/breakoutMarket"
import { breakoutMarketFactoryAbi } from "../../abi/generated/breakoutMarketFactory"
import { permitAbi } from "../../abi/permit"
import { getAccount, type ReadableClient } from "../../internal/contracts"
import { oddsToQuantities } from "../../internal/oddsToQuantities"
import type { PermitSignature } from "../../types"

// =============================================================================
// Types
// =============================================================================

export interface FactoryInfo {
	usdc: Address
	registry: Address
}

export interface MarketBatchConfig {
	salts: Hex[]
	liquidityParams: bigint[]
	bucketCount: number
	/** Optional metadata - not passed to contract */
	entityKeys?: Hex[]
	/** Optional metadata - not passed to contract */
	creators?: Address[]
	/**
	 * Optional initial odds per market (percentages summing to 100).
	 * Index corresponds to salt index. Undefined/missing entries use uniform odds.
	 * @example [[60, 40], undefined, [70, 30]] - first market 60/40, second uniform, third 70/30
	 */
	initialOdds?: (number[] | undefined)[]
}

export interface CreateMarketsWithPermitParams {
	factoryAddress: Address
	implementation: Address
	feeBps: bigint
	openTime: bigint
	cutoffTime: bigint
	batches: MarketBatchConfig[]
	/** EIP-2612 permit signature */
	permitSig: PermitSignature
	permitDeadline: bigint
	permitAmount: bigint
	/** USDC token address for permit call */
	usdcAddress: Address
	/** Public client for waiting on transaction receipts */
	publicClient: Client
}

// =============================================================================
// Factory Read Functions
// =============================================================================

/**
 * Get factory configuration info
 */
export async function getFactoryInfo(
	client: ReadableClient,
	factoryAddress: Address,
): Promise<FactoryInfo> {
	const [usdc, registry] = await Promise.all([
		readContract(client, {
			address: factoryAddress,
			abi: breakoutMarketFactoryAbi,
			functionName: "USDC",
		}),
		readContract(client, {
			address: factoryAddress,
			abi: breakoutMarketFactoryAbi,
			functionName: "REGISTRY",
		}),
	])

	return { usdc, registry }
}

/**
 * Predict market address for a given salt
 */
export async function predictMarketAddress(
	client: ReadableClient,
	factoryAddress: Address,
	implementation: Address,
	salt: Hex,
): Promise<Address> {
	return readContract(client, {
		address: factoryAddress,
		abi: breakoutMarketFactoryAbi,
		functionName: "predictMarketAddress",
		args: [implementation, salt],
	})
}

// =============================================================================
// Reserve Calculation
// =============================================================================

/**
 * Calculate reserve needed for a market with given liquidity parameter and bucket count.
 *
 * For UNIFORM markets (50/50, 33/33/33, etc.):
 * Uses the formula: reserve = floor(liquidityParam * ln(bucketCount)) + 1 USDC
 *
 * For SEEDED markets (custom odds), use calculateSeededReserve() instead.
 *
 * @param liquidityParam - The desired 'b' parameter (controls slippage). In dollars.
 * @param bucketCount - Number of outcomes (2 for binary, etc.)
 * @returns Reserve needed in USDC micro-units (6 decimals)
 */
export function calculateReserve(liquidityParam: bigint, bucketCount: number): bigint {
	const lnBuckets = Math.log(bucketCount)
	const maxLoss = Math.floor(Number(liquidityParam) * lnBuckets)
	// Add 1 USDC buffer (1e6 microdollars)
	return BigInt(maxLoss + 1) * 1_000_000n
}

/**
 * Calculate reserve needed for a SEEDED market with custom initial odds.
 *
 * When you seed a market with non-uniform odds (e.g., 80/20), additional reserve
 * is required to cover the "cost" of those initial positions. This prevents
 * insolvency when users buy large positions on the cheaper outcome.
 *
 * Formula: reserve = b * ln(Σexp(q_i/b))
 *
 * For uniform odds, this equals b * ln(n) (the standard formula).
 * For skewed odds, this is HIGHER than b * ln(n).
 *
 * @param liquidityParam - The desired 'b' parameter (controls slippage). In dollars.
 * @param quantities - Initial quantities from oddsToQuantities() in USDC micro-units
 * @returns Reserve needed in USDC micro-units (6 decimals)
 *
 * @example
 * // For 80/20 odds on binary market with b=$5000
 * // First calculate reserve from b: reserve = b * ln(2) ≈ $3466
 * const reserveDollars = BigInt(Math.round(5000 * Math.log(2))) // 3466n
 * const quantities = oddsToQuantities([80, 20], reserveDollars)
 * const reserve = calculateSeededReserve(5000n, quantities)
 * // reserve will be ~$4581 micro-USDC (vs ~$3466 for uniform 50/50)
 */
export function calculateSeededReserve(liquidityParam: bigint, quantities: bigint[]): bigint {
	if (quantities.length === 0) {
		throw new Error("Quantities array cannot be empty")
	}

	// Check if uniform (all zeros) - use standard formula
	const isUniform = quantities.every(q => q === 0n)
	if (isUniform) {
		return calculateReserve(liquidityParam, quantities.length)
	}

	// b in micro-USDC (6 decimals)
	const b = Number(liquidityParam) * 1_000_000

	// Calculate Σexp(q_i/b)
	let sumExp = 0
	for (const q of quantities) {
		const ratio = Number(q) / b
		sumExp += Math.exp(ratio)
	}

	// reserve = b * ln(sumExp)
	const reserve = b * Math.log(sumExp)

	// Add 1 USDC buffer and return as bigint
	return BigInt(Math.ceil(reserve)) + 1_000_000n
}

/**
 * Calculate total reserve needed for multiple market batches.
 *
 * Automatically accounts for seeded markets (custom odds) which require
 * more reserve than uniform markets.
 *
 * @param batches - Array of market batch configurations
 * @returns Total reserve needed in USDC micro-units (6 decimals)
 */
export function calculateTotalReserve(batches: MarketBatchConfig[]): bigint {
	let total = 0n
	for (const batch of batches) {
		for (let i = 0; i < batch.salts.length; i++) {
			const liquidityParam = batch.liquidityParams[i] ?? batch.liquidityParams[0] ?? 0n
			const odds = batch.initialOdds?.[i]

			let reserve: bigint
			if (odds && odds.length > 0) {
				// Seeded market - calculate with quantities
				// oddsToQuantities expects reserve in dollars (not b/liquidityParam)
				// reserve = b * ln(n), so we compute it from liquidityParam
				const reserveForQuantities = BigInt(
					Math.round(Number(liquidityParam) * Math.log(batch.bucketCount)),
				)
				const quantities = oddsToQuantities(odds, reserveForQuantities)
				reserve = calculateSeededReserve(liquidityParam, quantities)
			} else {
				// Uniform market
				reserve = calculateReserve(liquidityParam, batch.bucketCount)
			}
			total += reserve
		}
	}
	return total
}

// =============================================================================
// Market Creation
// =============================================================================

/**
 * Encode market initialization data using the BreakoutMarket ABI
 *
 * @param usdcAddress - USDC token address
 * @param feeBps - Fee in basis points (e.g., 150 = 1.5%)
 * @param openTime - Unix timestamp when market opens for trading
 * @param cutoffTime - Unix timestamp when trading closes
 * @param bucketCount - Number of outcomes (2 for binary)
 * @param liquidityParam - The 'b' parameter in micro-USDC (controls slippage). Pass 0n to derive from reserve.
 * @param initialQuantities - Initial quantities for seeded markets (must sum to zero)
 */
export function encodeMarketInitData(
	usdcAddress: Address,
	feeBps: bigint,
	openTime: bigint,
	cutoffTime: bigint,
	bucketCount: number,
	liquidityParam: bigint,
	initialQuantities: bigint[] = [],
): Hex {
	return encodeFunctionData({
		abi: breakoutMarketAbi,
		functionName: "initialize",
		args: [
			usdcAddress,
			feeBps,
			openTime,
			cutoffTime,
			bucketCount,
			liquidityParam,
			initialQuantities,
		],
	})
}

/**
 * Create multiple markets using factory multicall with USDC permit
 *
 * This creates all markets by:
 * 1. Calling USDC.permit() to approve the factory
 * 2. Calling factory.multicall() with batched createMarket calls
 */
export async function createAllMarketsWithPermit(
	client: WalletClient,
	params: CreateMarketsWithPermitParams,
): Promise<Hash> {
	const account = await getAccount(client)

	// Step 1: Call USDC permit to approve factory and wait for confirmation
	const permitHash = await writeContract(client, {
		address: params.usdcAddress,
		abi: permitAbi,
		functionName: "permit",
		args: [
			account, // owner
			params.factoryAddress, // spender
			params.permitAmount, // value
			params.permitDeadline, // deadline
			params.permitSig.v, // v
			params.permitSig.r, // r
			params.permitSig.s, // s
		],
		account,
		chain: client.chain,
	})

	// Wait for permit transaction to be confirmed
	await waitForTransactionReceipt(params.publicClient, { hash: permitHash })

	// Build multicall data for all markets
	const calls: Hex[] = []

	for (const batch of params.batches) {
		for (let i = 0; i < batch.salts.length; i++) {
			const salt = batch.salts[i]
			const liquidityParam = batch.liquidityParams[i]

			if (!salt || liquidityParam === undefined) {
				continue
			}

			const odds = batch.initialOdds?.[i]
			let reserve: bigint
			let initialQuantities: bigint[] = []

			// Convert liquidityParam from dollars to micro-USDC (6 decimals)
			const liquidityParamMicroUsdc = liquidityParam * 1_000_000n

			if (odds && odds.length > 0) {
				// Calculate quantities using the target liquidityParam
				// q_i = b * (ln(p_i) - average(ln(p)))
				const probs = odds.map(o => o / 100)
				const logGeomMean = probs.reduce((acc, p) => acc + Math.log(p), 0) / probs.length
				const bMicroUsdc = Number(liquidityParamMicroUsdc)

				initialQuantities = probs.map(p => {
					const q = bMicroUsdc * (Math.log(p) - logGeomMean)
					return BigInt(Math.round(q))
				})

				// Ensure quantities sum to exactly zero (fix rounding errors)
				const sum = initialQuantities.reduce((a, b) => a + b, 0n)
				if (sum !== 0n && initialQuantities.length > 0) {
					initialQuantities[0] = (initialQuantities[0] ?? 0n) - sum
				}

				// Check if uniform (all zeros)
				const isUniform = initialQuantities.every(q => q === 0n)

				if (isUniform) {
					// Uniform odds - use standard reserve calculation
					reserve = calculateReserve(liquidityParam, batch.bucketCount)
					initialQuantities = []
				} else {
					// Seeded market - calculate the reserve needed for solvency
					reserve = calculateSeededReserve(liquidityParam, initialQuantities)
				}
			} else {
				// Uniform market - no custom odds
				reserve = calculateReserve(liquidityParam, batch.bucketCount)
			}

			const initData = encodeMarketInitData(
				params.usdcAddress,
				params.feeBps,
				params.openTime,
				params.cutoffTime,
				batch.bucketCount,
				liquidityParamMicroUsdc,
				initialQuantities,
			)

			const createMarketCall = encodeFunctionData({
				abi: breakoutMarketFactoryAbi,
				functionName: "createMarket",
				args: [params.implementation, salt, reserve, initData],
			})

			calls.push(createMarketCall)
		}
	}

	// Step 2: Execute multicall to create all markets
	return client.writeContract({
		address: params.factoryAddress,
		abi: breakoutMarketFactoryAbi,
		functionName: "multicall",
		args: [calls],
		account,
		chain: client.chain,
	})
}
